import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Project, WorkItem, WorkflowDefinition } from '@captain-tasko/shared';
import { apiList, apiSingle, ApiError } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { useRealtime } from '../lib/realtime.js';
import { toast } from '../components/Toast.js';
import { PriorityPill } from '../components/PriorityPill.js';
import { formatWorkItemKey } from '../lib/format.js';

type ColumnView = 'all' | 'non-empty';
const STORAGE_KEY = 'tasko.board.columnView';

// Subtle background tint per stage so the eye can scan stage groups quickly.
const STAGE_TINT: Record<string, string> = {
  backlog: 'rgba(142, 139, 129, 0.08)',
  unstarted: 'rgba(42, 92, 140, 0.08)',
  started: 'rgba(201, 95, 31, 0.10)',
  completed: 'rgba(47, 107, 78, 0.10)',
  cancelled: 'rgba(92, 90, 82, 0.06)',
};

export function WorkItemBoard({ onNavigate }: { onNavigate: (p: string) => void }) {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<string>('');
  const [columnView, setColumnView] = useState<ColumnView>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return (stored === 'non-empty' ? 'non-empty' : 'all') as ColumnView;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, columnView);
  }, [columnView]);

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiList<Project>('/projects'),
  });
  const workflows = useQuery({
    queryKey: ['workflows'],
    queryFn: () => apiList<WorkflowDefinition>('/workflows'),
  });
  const items = useQuery({
    queryKey: ['workitems', { projectId, view: 'board' }],
    queryFn: () => apiList<WorkItem>('/workitems', { query: { projectId: projectId || undefined, limit: 500 } }),
  });

  useRealtime({
    types: ['work.created', 'work.updated', 'work.status_changed', 'work.deleted'],
    onEvent: () => qc.invalidateQueries({ queryKey: ['workitems'] }),
  });

  const workflow = useMemo(() => {
    if (!workflows.data) return null;
    if (projectId && projects.data) {
      const proj = projects.data.data.find((p) => p.id === projectId);
      if (proj) return workflows.data.data.find((w) => w.id === proj.defaultWorkflowId);
    }
    return workflows.data.data.find((w) => w.isDefault) ?? workflows.data.data[0];
  }, [workflows.data, projects.data, projectId]);

  const projectById = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [projects.data]);

  const transition = useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) =>
      apiSingle(`/workitems/${id}/transition`, { method: 'POST', body: { to } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workitems'] }),
    onError: (e: Error) => {
      if (e instanceof ApiError && Array.isArray(e.details)) {
        const reasons = (e.details as { message?: string; guard?: string }[])
          .map((d) => d.message ?? d.guard)
          .join('; ');
        toast.error(`Cannot transition: ${reasons || e.message}`);
      } else {
        toast.error(e.message);
      }
    },
  });

  if (items.isError || workflows.isError) {
    const err = (items.error ?? workflows.error) as Error | undefined;
    const detail = err instanceof ApiError && Array.isArray(err.details)
      ? (err.details as { path?: string; issue?: string; message?: string }[])
          .map((d) => `${d.path ?? '(no path)'}: ${d.issue ?? d.message ?? 'unknown'}`)
          .join('; ')
      : null;
    return (
      <EmptyState
        title="Couldn't load the board"
        body={detail ? `${err?.message ?? 'API error'} — ${detail}` : (err?.message ?? 'unknown')}
      />
    );
  }
  if (items.isLoading || workflows.isLoading) return <SkeletonRows rows={6} />;
  if (!workflow) {
    return (
      <EmptyState
        title="No workflow available"
        body="The default workflow hasn't been seeded. Run: pnpm db:seed"
      />
    );
  }

  const knownStatusIds = new Set(workflow.states.map((s) => s.id));
  const itemsByStatus = new Map<string, WorkItem[]>();
  const orphans: WorkItem[] = [];

  for (const it of items.data?.data ?? []) {
    if (knownStatusIds.has(it.status)) {
      const arr = itemsByStatus.get(it.status) ?? [];
      arr.push(it);
      itemsByStatus.set(it.status, arr);
    } else {
      orphans.push(it);
    }
  }

  const orderedStates = workflow.states.slice().sort((a, b) => a.position - b.position);
  const visibleStates = columnView === 'all'
    ? orderedStates
    : orderedStates.filter((s) => (itemsByStatus.get(s.id)?.length ?? 0) > 0);

  const totalItems = items.data?.data.length ?? 0;
  const bucketedCount = totalItems - orphans.length;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Board</h1>
          <div className="page-subtitle">
            {totalItems} items · {bucketedCount} in workflow columns
            {orphans.length > 0 && <span style={{ color: 'var(--warning-primary)' }}> · {orphans.length} uncategorized</span>}
            {' · '}drag cards between columns to transition
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ColumnViewToggle value={columnView} onChange={setColumnView} hiddenCount={orderedStates.length - visibleStates.length} />
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={{ padding: 6, borderRadius: 6, border: '1px solid var(--border-strong)' }}
          >
            <option value="">All projects</option>
            {projects.data?.data.map((p) => (
              <option key={p.id} value={p.id}>{p.key} — {p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {totalItems === 0 && (
        <EmptyState
          title="No work items yet"
          body="Create a project, then add a work item from the Work items list."
          action={<button className="btn btn--create" onClick={() => onNavigate('/workitems')}>Go to work items</button>}
        />
      )}

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16 }}>
        {visibleStates.map((state) => (
          <BoardColumn
            key={state.id}
            state={state}
            items={itemsByStatus.get(state.id) ?? []}
            projectById={projectById}
            onDropCard={(itemId) => transition.mutate({ id: itemId, to: state.id })}
            onCardClick={(itemId) => onNavigate(`/workitems/${itemId}`)}
          />
        ))}

        {orphans.length > 0 && (
          <OrphanColumn
            items={orphans}
            workflowStates={workflow.states.map((s) => s.id)}
            projectById={projectById}
            onMove={(itemId, to) => transition.mutate({ id: itemId, to })}
            onCardClick={(itemId) => onNavigate(`/workitems/${itemId}`)}
          />
        )}
      </div>

      {orphans.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: '#FFF7E6',
            border: '1px solid var(--warning-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
          }}
        >
          <strong>Why are some items uncategorized?</strong>{' '}
          These items have a <code>status</code> value that doesn't match any state in the current workflow.
          Use the "Move to…" dropdown on each card to move them into a real state.
        </div>
      )}
    </>
  );
}

function ColumnViewToggle({
  value,
  onChange,
  hiddenCount,
}: {
  value: ColumnView;
  onChange: (v: ColumnView) => void;
  hiddenCount: number;
}) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 6, overflow: 'hidden' }}>
      <button
        onClick={() => onChange('all')}
        style={{
          padding: '6px 10px',
          fontSize: 12,
          background: value === 'all' ? 'var(--accent-primary)' : 'var(--surface-raised)',
          color: value === 'all' ? '#fff' : 'var(--text-primary)',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        All columns
      </button>
      <button
        onClick={() => onChange('non-empty')}
        style={{
          padding: '6px 10px',
          fontSize: 12,
          background: value === 'non-empty' ? 'var(--accent-primary)' : 'var(--surface-raised)',
          color: value === 'non-empty' ? '#fff' : 'var(--text-primary)',
          border: 'none',
          borderLeft: '1px solid var(--border-strong)',
          cursor: 'pointer',
        }}
        title={hiddenCount > 0 && value === 'non-empty' ? `${hiddenCount} empty column(s) hidden` : undefined}
      >
        Non-empty only{hiddenCount > 0 && value === 'non-empty' && <span style={{ marginLeft: 4, opacity: .7 }}>({hiddenCount})</span>}
      </button>
    </div>
  );
}

function BoardColumn({
  state,
  items,
  projectById,
  onDropCard,
  onCardClick,
}: {
  state: WorkflowDefinition['states'][number];
  items: WorkItem[];
  projectById: Map<string, Project>;
  onDropCard: (itemId: string) => void;
  onCardClick: (itemId: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const tint = STAGE_TINT[state.stage] ?? 'var(--surface-sunken)';
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const id = e.dataTransfer.getData('text/work-item-id');
        if (id) onDropCard(id);
      }}
      style={{
        // Fixed width + flex: 0 0 — columns stay uniform regardless of how
        // many columns are visible. Without flex:0 0, a flex parent with
        // overflow-x:auto still stretches children to fill the row when
        // there are few of them. With flex:0 0 they keep their basis and
        // overflow horizontally as designed.
        flex: '0 0 320px',
        width: 320,
        background: dragOver ? '#F0F6F2' : tint,
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${dragOver ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
        padding: 12,
        borderTop: `3px solid ${state.color}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: state.color, display: 'inline-block' }} />
          {state.label}
        </strong>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{items.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it) => (
          <ItemCard
            key={it.id}
            item={it}
            project={projectById.get(it.projectId) ?? null}
            onClick={() => onCardClick(it.id)}
          />
        ))}
        {items.length === 0 && (
          <div style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
            Drop here
          </div>
        )}
      </div>
    </div>
  );
}

function OrphanColumn({
  items,
  workflowStates,
  projectById,
  onMove,
  onCardClick,
}: {
  items: WorkItem[];
  workflowStates: string[];
  projectById: Map<string, Project>;
  onMove: (itemId: string, to: string) => void;
  onCardClick: (itemId: string) => void;
}) {
  return (
    <div
      style={{
        flex: '0 0 320px',
        width: 320,
        background: '#FFF7E6',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--warning-primary)',
        padding: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>⚠ Uncategorized</strong>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{items.length}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
        Status doesn't match a workflow state.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it) => {
          const project = projectById.get(it.projectId);
          return (
            <div
              key={it.id}
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--warning-primary)',
                borderRadius: 6,
                padding: 10,
                borderLeft: `3px solid ${project?.color ?? 'var(--text-muted)'}`,
              }}
            >
              <div
                // 0.1.51 — key color derived from project.color.
                style={{
                  fontSize: 11,
                  color: project?.color ?? 'var(--text-muted)',
                  fontWeight: project?.color ? 600 : undefined,
                  fontFamily: 'var(--font-mono)',
                }}
                title={formatWorkItemKey(it).secondary ?? undefined}
              >
                {formatWorkItemKey(it).primary}
              </div>
              <div style={{ fontWeight: 500, marginTop: 2, cursor: 'pointer' }} onClick={() => onCardClick(it.id)}>
                {it.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--warning-primary)', marginTop: 4 }}>
                Current status: <code>{it.status}</code>
              </div>
              <div style={{ marginTop: 8 }}>
                <select
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) onMove(it.id, e.target.value); }}
                  style={{ width: '100%', padding: 4, fontSize: 12 }}
                >
                  <option value="">Move to…</option>
                  {workflowStates.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ItemCard({
  item,
  project,
  onClick,
}: {
  item: WorkItem;
  project: Project | null;
  onClick: () => void;
}) {
  const projectColor = project?.color ?? 'var(--text-muted)';
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/work-item-id', item.id)}
      onClick={onClick}
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 6,
        padding: 10,
        cursor: 'grab',
        boxShadow: 'var(--shadow-sm)',
        borderLeft: `4px solid ${projectColor}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
        <div
          // 0.1.51 — key text color is the item's project.color (matches
          // WorkItemsList / Watching / Dashboard / WorkItemDetail fix).
          // Falls back to --text-muted for items whose project has no
          // color set. Dot + text now share the color so the key reads
          // as "belonging to project X" without opening a tooltip.
          style={{
            fontSize: 11,
            color: project?.color ?? 'var(--text-muted)',
            fontWeight: project?.color ? 600 : undefined,
            fontFamily: 'var(--font-mono)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
          title={formatWorkItemKey(item).secondary ?? undefined}
        >
          {project && (
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: projectColor, display: 'inline-block' }} />
          )}
          {formatWorkItemKey(item).primary}
        </div>
        <PriorityPill priority={item.priority} />
      </div>
      <div style={{ fontWeight: 500, marginTop: 4, lineHeight: 1.3 }}>{item.title}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>{item.assigneeIds.length > 0 ? `${item.assigneeIds.length} assignee${item.assigneeIds.length > 1 ? 's' : ''}` : '—'}</span>
        {item.dueDate && <span>due {new Date(item.dueDate).toLocaleDateString()}</span>}
      </div>
      {item.labels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {item.labels.slice(0, 4).map((l) => <span key={l} className="tag-chip">{l}</span>)}
          {item.labels.length > 4 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>+{item.labels.length - 4}</span>}
        </div>
      )}
    </div>
  );
}
